import {
  Box,
  Button,
  Flex,
  ColorsV2,
  Banner,
  Card,
  useThemeColors,
  OneColumnLayout,
  Header,
} from '@electricjs/arc';
import electricLogo from '@/assets/electric_logo_red.svg';
import {
  Auth0CandidateUser,
  HandleAccountLinkArgs,
  PrimaryConnections,
} from './auth0-link-user-types';
import { UserProfile, UserProfilesLinkVisual } from './UserProfiles';
import { Auth0Client, IdToken, User } from '@auth0/auth0-spa-js';

const AccountLinking = ({
  userToAuth,
  currentUser,
  handleAccountLink,
  onSkipAccountLink,
}: {
  userToAuth: Auth0CandidateUser;
  currentUser: User;
  handleAccountLink: (args: HandleAccountLinkArgs) => void;
  onSkipAccountLink: (connection: string, email: string) => void;
}) => {
  const authenticateUser = async () => {
    // We leverage the Auth0Client to authenticate the user so that
    // it does not interfere with our session from the Auth0Provider
    // in main.js. This allows us access to both user tokens simultaneously.
    const a0 = new Auth0Client({
      domain: window._env_?.VITE_AUTH0_DOMAIN,
      clientId: window._env_?.VITE_AUTH0_CLIENT_ID,
      useRefreshTokens: true,
    });
    const connection = userToAuth?.identities[0]?.connection;
    const authorizationParams = {
      audience: window._env_?.VITE_AUTH0_AUDIENCE,
      scope:
        'openid email profile read:current_user update:current_user_identities',
      connection,
      // Encode the account linking flag into login_hint using | as a sentinel
      // (| is not a valid email character per RFC 5321). ACUL parses this to
      // detect account linking flows since ext- params are not forwarded by
      // Auth0 into untrustedData.authorizationParams for popup flows.
      // Only append the sentinel for non-social connections — social providers
      // bypass ACUL entirely and receive login_hint directly, so a mangled email
      // would break account pre-selection on the provider's login page.
      // TODO: Once Auth0 fixes ext- param forwarding in popup flows, switch back to:
      //   login_hint: userToAuth?.email,
      //   'ext-accountLinking': 'true',
      login_hint: userToAuth?.email
        ? userToAuth.identities[0]?.isSocial
          ? userToAuth.email
          : `${userToAuth.email}|accountLinking`
        : undefined,
      // `accountLinking` is read by the Auth0 post-login action via
      // event.request.query.accountLinking to enforce MFA on staging.
      accountLinking: true,
    };
    // Prompt the user to login via a popup rather than a redirect so that
    // we don't lose our current session info.
    await a0.loginWithPopup({
      authorizationParams,
    });
    const idTokenClaims = (await a0.getIdTokenClaims()) as IdToken;
    const accessToken = await a0.getTokenSilently({ authorizationParams });

    // Determine which user should be the primary account.
    // The primary should be the user whose Auth0 id matches the
    // Auth0 id provided by the client in the custom claim.
    const currentUserAuth0IdFromClient =
      currentUser['https://my.electric.ai/currentUserAuth0Id'];

    const getAuth0Id = (
      user: Auth0CandidateUser | User
    ): string | undefined => {
      const candidateUser = user as Auth0CandidateUser;
      return candidateUser.sub ?? candidateUser.user_id;
    };

    const candidateAuth0Id = getAuth0Id(userToAuth);
    const currentAuth0Id = getAuth0Id(currentUser as Auth0CandidateUser);

    // Default: keep previous behavior where Electric-style connections are primary.
    let makePrimary =
      connection === PrimaryConnections.UsernamePassword ||
      connection === PrimaryConnections.Electric ||
      connection === PrimaryConnections.LegacyElectric;

    if (currentUserAuth0IdFromClient) {
      if (candidateAuth0Id === currentUserAuth0IdFromClient) {
        // Candidate user matches the DB auth0 id → candidate is primary
        makePrimary = true;
      } else if (currentAuth0Id === currentUserAuth0IdFromClient) {
        // Current user matches the DB auth0 id → current user is primary
        makePrimary = false;
      }
      // If neither matches, keep the Electric-first fallback above.
    }

    return {
      idTokenClaims,
      accessToken,
      makePrimary,
    };
  };

  const handleSkip = () => {
    const connection = userToAuth?.identities[0]?.connection;
    const email = userToAuth?.email;
    if (!connection || !email) {
      console.warn(
        '[AccountLinking] Missing connection or email, cannot skip account linking'
      );
      return;
    }
    onSkipAccountLink(connection, email);
  };

  const authenticateAndLinkUser = async () => {
    // We need to challenge the user to log into the account they
    // are linking to in order to verify they have access to it.
    try {
      const {
        idTokenClaims: {
          __raw: targetUserIDToken,
          sub: targetUserIdWithProvider,
        },
        accessToken,
        makePrimary,
      } = await authenticateUser();

      const targetUserId = targetUserIdWithProvider;
      handleAccountLink({
        targetUserIDToken,
        targetUserAccessToken: accessToken,
        targetUserId,
        makePrimary,
      });
    } catch (err) {
      console.log('Error linking account', err);
    }
  };

  return (
    <AccountLinkingPage
      currentUser={currentUser}
      userToAuth={userToAuth}
      handleAccountLink={authenticateAndLinkUser}
      onSkip={handleSkip}
    />
  );
};

type AccountLinkingPageProps = {
  currentUser?: Auth0CandidateUser | User;
  candidateIdentitiesj?: Auth0CandidateUser[];
  userToAuth?: Auth0CandidateUser;
  handleAccountLink: () => void;
  onSkip: () => void;
};

export const AccountLinkingPage = ({
  currentUser,
  userToAuth,
  handleAccountLink,
  onSkip,
}: AccountLinkingPageProps) => {
  const [borderColor] = useThemeColors([ColorsV2.BORDER]);
  return (
    <Box
      display="flex"
      flexDirection="column"
      height={'100vh'}
      backgroundColor={ColorsV2.BACKGROUND}
      width="100vw"
      overflow="hidden">
      <Box
        display="flex"
        flexDirection="row"
        padding="2.4rem"
        borderBottom={`solid 1px ${borderColor}`}>
        <img src={electricLogo} alt="Electric" height="42px" />
      </Box>
      <OneColumnLayout>
        <Header
          title="Link Your Existing Account"
          subtitle="It appears you already have an Electric IT Management account."
        />
        {currentUser && userToAuth && (
          <UserProfilesLinkVisual
            currentUser={currentUser as Auth0CandidateUser}
            candidateUser={userToAuth}
          />
        )}
        <Flex vertical padding="3.2rem" alignItems="center">
          {' '}
          <Banner
            id="account-link-banner"
            isFlat
            message="Please authenticate with the following user credentials to link this
        user."></Banner>
          <Flex justifyContent="center" paddingTop="2.4rem">
            <Card id="account-link-card" elevated>
              <form>
                <Flex
                  justifyContent="space-evenly"
                  alignItems="center"
                  padding="1.2rem">
                  <Flex vertical alignItems="center">
                    <Flex paddingBottom="1.2rem">
                      {userToAuth && <UserProfile {...userToAuth} />}
                    </Flex>
                    <Flex vertical alignItems="center" gap="1.6rem">
                      <Button
                        id="account-link-button"
                        onClick={handleAccountLink}>
                        Link account
                      </Button>
                      <Button
                        id="account-link-skip-button"
                        variant="text"
                        onClick={onSkip}>
                        Log in with my existing account instead
                      </Button>
                    </Flex>
                  </Flex>
                </Flex>
              </form>
            </Card>
          </Flex>
        </Flex>
      </OneColumnLayout>
    </Box>
  );
};

export default AccountLinking;
